/**
 * №53.3.1 S0 (D-CR26 item 4) — THE RECORD TABS' READING ORDER.
 *
 * "Ordering is the declared rule: subject spine first (Process · Billing ·
 * Trust), joins by count descending, Details last. Declaration order / install
 * order is never the order." `record-subviews.ts`'s `DECLARED` table is a
 * DECLARATION of which sub-views a type has — the array's own position was
 * never meant to be read as display order, and until this rung nothing else
 * decided a different one, so `openRecordNav` (route-anchor-matrix.ts) just
 * handed the declaration order straight to the tab strip. This file is the
 * missing second step: reorder what `openRecordNav` returns before it renders.
 *
 * Pure and framework-free so it can be asserted directly — no DOM, no router.
 */

/** Stream is always first, whatever else a type declares. */
const STREAM_KEY = "place-record";

/** The three subject-spine surfaces, in their own fixed order — never sorted
 *  by count. A type that declares none of them (or only some) just has a
 *  shorter fixed prefix; nothing pads the gap. */
const SPINE_ORDER: readonly string[] = ["place-process", "place-record-billing", "place-record-trust"];

const PROGRAM_KEY = "place-program";

/** Details is always last — the record's declared-section overflow, never a
 *  candidate for the count sort. */
const DETAILS_KEY = "place-record-details";

/**
 * Reorder a record's sub-view items per D-CR26: Stream, then the spine
 * (Process, Billing, Trust — whichever exist), then every remaining "join"
 * tab sorted by its own count descending (ties keep their incoming relative
 * order — `Array.prototype.sort` is stable, and the incoming order is already
 * `record-subviews.ts`'s declaration, which is as good a tie-break as any),
 * then Program, then Details last.
 *
 * `counts` is the SAME `SubViewCounts` channel the tab badges already read
 * (`stage-head.tsx`) — an entry with no count sorts as 0, which is correct: an
 * uncounted join (nothing has loaded yet, or the type declares no count for
 * it) never outranks one the product can prove is busier.
 */
export function orderRecordTabs<T extends { key: string }>(
  items: readonly T[],
  counts: Readonly<Record<string, number | undefined>>,
): T[] {
  const byKey = new Map(items.map((it) => [it.key, it] as const));
  const placed = new Set<string>();
  const out: T[] = [];

  const take = (key: string) => {
    const it = byKey.get(key);
    if (it && !placed.has(key)) {
      out.push(it);
      placed.add(key);
    }
  };

  take(STREAM_KEY);
  for (const key of SPINE_ORDER) take(key);

  const joins = items.filter((it) => !placed.has(it.key) && it.key !== PROGRAM_KEY && it.key !== DETAILS_KEY);
  joins.sort((a, b) => (counts[b.key] ?? 0) - (counts[a.key] ?? 0));
  for (const it of joins) {
    out.push(it);
    placed.add(it.key);
  }

  take(PROGRAM_KEY);
  take(DETAILS_KEY);

  // Anything genuinely unaccounted for (should not happen — every key is one
  // of the four buckets above) rides at the end in its incoming order rather
  // than silently vanishing from the strip.
  for (const it of items) if (!placed.has(it.key)) out.push(it);

  return out;
}
